import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { FlaskConical, Calendar, Building2, TrendingUp, AlertTriangle, RefreshCw } from 'lucide-react';

const TrialAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [outlets, setOutlets] = useState([]);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOutlet, setSelectedOutlet] = useState('');

  const isOrgAdmin = user?.is_org_admin || user?.is_super_admin || user?.org_roles?.includes('ORG_ADMIN');

  useEffect(() => {
    loadOutlets();
    loadTrialsSummary();
  }, []);

  const loadOutlets = async () => {
    try {
      const response = await api.getOutlets();
      setOutlets(response.data || []);
    } catch (err) {
      console.error('Error loading outlets:', err);
    }
  };

  const loadTrialsSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedOutlet) params.outlet_id = selectedOutlet;
      
      const response = await api.getTrialsSummary(params);
      setSummary(response.data);
    } catch (err) {
      console.error('Error loading trials summary:', err);
      setError(err.response?.data?.detail || 'Failed to load trials data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadTrialsSummary();
  };

  if (!isOrgAdmin) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertTriangle className="mx-auto text-red-600 mb-4" size={48} />
          <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700">Only organization administrators can view trial analytics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="trial-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FlaskConical className="text-purple-600" size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Trial Analytics</h1>
              <p className="text-gray-600">Track trial spend across outlets and ingredients</p>
            </div>
          </div>
        </div>
        <button
          onClick={loadTrialsSummary}
          disabled={loading}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Building2 size={14} className="inline mr-1" />
              Outlet
            </label>
            <select
              value={selectedOutlet}
              onChange={(e) => setSelectedOutlet(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 min-w-[200px]"
            >
              <option value="">All Outlets</option>
              {outlets.map(outlet => (
                <option key={outlet.outlet_id} value={outlet.outlet_id}>
                  {outlet.outlet_name || outlet.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleFilter}
            disabled={loading}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trial analytics...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertTriangle className="mx-auto text-red-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Data</h3>
          <p className="text-red-700">{error}</p>
          <button
            onClick={loadTrialsSummary}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      ) : summary ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="text-purple-600" size={20} />
                </div>
                <p className="text-sm text-gray-500">Total Trial Spend</p>
              </div>
              <p className="text-3xl font-bold text-purple-900">
                ₹{(summary.total_trial_spend || 0).toLocaleString()}
              </p>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FlaskConical className="text-blue-600" size={20} />
                </div>
                <p className="text-sm text-gray-500">Trial Bills</p>
              </div>
              <p className="text-3xl font-bold text-blue-900">
                {summary.trial_bill_count || 0}
              </p>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Building2 className="text-green-600" size={20} />
                </div>
                <p className="text-sm text-gray-500">Outlets with Trials</p>
              </div>
              <p className="text-3xl font-bold text-green-900">
                {Object.keys(summary.by_outlet || {}).length || '-'}
              </p>
            </div>
          </div>

          {/* Message if using local aggregation */}
          {summary.message && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <AlertTriangle size={16} className="inline mr-2" />
                {summary.message}
              </p>
            </div>
          )}

          {/* Ingredient-level breakdown (if available) */}
          {summary.by_ingredient && Object.keys(summary.by_ingredient).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Trial Spend by Ingredient</h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingredient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(summary.by_ingredient).map(([ingredientId, data]) => (
                    <tr key={ingredientId}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {data.name || ingredientId}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {data.quantity} {data.unit}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-purple-600">
                        ₹{(data.amount || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty state for ingredient breakdown */}
          {(!summary.by_ingredient || Object.keys(summary.by_ingredient).length === 0) && (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <FlaskConical className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">No ingredient-level breakdown available</p>
              <p className="text-sm text-gray-500 mt-1">
                Detailed breakdown requires Vendor Engine trials summary endpoint
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <FlaskConical className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">No trial data available</p>
        </div>
      )}
    </div>
  );
};

export default TrialAnalytics;
